import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Users, Image as ImageIcon, Search, Plus, Trash2, Edit3, Save, X,
  CheckCircle, AlertCircle, Loader2, Eye, EyeOff, Upload, Shield
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { adminApi, useAdminAuth } from '../context/AdminAuthContext';

const T = { vert: '#1e4a1a', or: '#c4a76c', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', border: '#e8e5e0' };
const CARD = { background: '#ffffff', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const INPUT_STYLE = { background: '#f5f3ef', border: `1px solid ${T.border}`, color: T.textPrimary, fontFamily: "'Montserrat', sans-serif" };

const TABS = [
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'config', label: 'Configuration', icon: Settings },
  { id: 'media', label: 'Medias', icon: ImageIcon },
];

const AdminSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <AdminLayout>
      <div className="max-w-7xl" data-testid="admin-settings-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Parametres</h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Utilisateurs, configuration et medias</p>
        </div>

        <div className="flex gap-1 mb-8 p-1 rounded-xl" style={{ background: '#f0ede8' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`settings-tab-${tab.id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-medium transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: activeTab === tab.id ? '#ffffff' : 'transparent', color: activeTab === tab.id ? T.vert : T.textMuted, boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'config' && <ConfigTab />}
        {activeTab === 'media' && <MediaTab />}
      </div>
    </AdminLayout>
  );
};

/* ━━━ USERS ━━━ */
const UsersTab = () => {
  const { user: currentUser } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'editor' });
  const [showPwd, setShowPwd] = useState(false);

  const load = useCallback(async () => { setLoading(true); try { setUsers(await adminApi.get('/api/admin/users')); } catch (err) { console.error('Failed to load users:', err); } setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: '', email: '', password: '', role: 'editor' }); setEditing(null); setShowModal(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: '', role: u.role }); setEditing(u.user_id); setShowModal(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) await adminApi.put(`/api/admin/users/${editing}`, form);
      else await adminApi.post('/api/admin/users', form);
      await load(); setShowModal(false);
    } catch (err) { console.error('Failed to save user:', err); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try { await adminApi.delete(`/api/admin/users/${id}`); await load(); } catch (err) { console.error('Failed to delete user:', err); }
  };

  return (
    <div className="space-y-6" data-testid="users-tab">
      <div className="flex justify-between items-center">
        <p className="text-[13px]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textSecondary }}>{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
        <button onClick={openNew} data-testid="add-user-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white"
          style={{ background: T.vert, fontFamily: "'Montserrat', sans-serif" }}>
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div> : (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.user_id} className="rounded-2xl p-5 flex items-center justify-between bg-white" style={CARD}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: u.role === 'admin' ? 'rgba(196,167,108,0.1)' : 'rgba(30,74,26,0.06)' }}>
                  <Shield className="w-4 h-4" style={{ color: u.role === 'admin' ? T.or : T.vert }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{u.name}</p>
                  <p className="text-[12px]" style={{ color: T.textMuted }}>{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full"
                  style={{ background: u.role === 'admin' ? 'rgba(196,167,108,0.1)' : '#f5f3ef', color: u.role === 'admin' ? T.or : T.textMuted }}>
                  {u.role}
                </span>
                <button onClick={() => openEdit(u)} className="p-2 rounded-lg transition-colors" style={{ color: T.textMuted }}><Edit3 className="w-4 h-4" /></button>
                {u.user_id !== currentUser?.user_id && u.role !== 'admin' && (
                  <button onClick={() => handleDelete(u.user_id)} className="p-2 rounded-lg" style={{ color: '#ef4444' }}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{editing ? 'Modifier' : 'Nouvel utilisateur'}</h2>
              <button onClick={() => setShowModal(false)} style={{ color: T.textMuted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Nom</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
              </div>
              <div className="relative">
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
                  Mot de passe {editing && '(vide = inchange)'}
                </label>
                <input type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-9" style={{ color: T.textMuted }}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE}>
                  <option value="admin">Administrateur</option>
                  <option value="editor">Editeur</option>
                </select>
              </div>
              <button onClick={handleSave} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-semibold text-white transition-all"
                style={{ background: T.vert, fontFamily: "'Montserrat', sans-serif" }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing ? 'Enregistrer' : 'Creer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ━━━ CONFIG ━━━ */
const ConfigTab = () => {
  const [config, setConfig] = useState({
    site_name: 'One Green Dev',
    contact_email: '',
    contact_phone: '',
    whatsapp_number: '',
    address: '',
    social_links: { facebook: '', instagram: '', linkedin: '' },
    seo: { meta_title: '', meta_description: '' },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => { (async () => { try { const d = await adminApi.get('/api/admin/config'); if (d) setConfig(d); } catch (err) { console.error('Failed to load config:', err); } setLoading(false); })(); }, []);
  const save = async () => { setSaving(true); try { await adminApi.put('/api/admin/config', config); setToast('Configuration sauvegardee'); setTimeout(() => setToast(''), 3000); } catch (err) { console.error('Failed to save config:', err); } setSaving(false); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div>;

  const Field = ({ label, value, onChange, type = 'text', area }) => (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>{label}</label>
      {area ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="config-tab">
      {toast && (
        <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.15)', color: '#16a34a' }}>
          <CheckCircle className="w-4 h-4" /> <span className="text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{toast}</span>
        </div>
      )}

      <div className="rounded-2xl p-6 bg-white" style={CARD}>
        <h3 className="text-sm font-semibold mb-5" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>Informations generales</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Nom du site" value={config.site_name} onChange={v => setConfig({ ...config, site_name: v })} />
          <Field label="Email de contact" value={config.contact_email} type="email" onChange={v => setConfig({ ...config, contact_email: v })} />
          <Field label="Telephone" value={config.contact_phone} onChange={v => setConfig({ ...config, contact_phone: v })} />
          <Field label="WhatsApp" value={config.whatsapp_number} onChange={v => setConfig({ ...config, whatsapp_number: v })} />
          <div className="sm:col-span-2"><Field label="Adresse" value={config.address} area onChange={v => setConfig({ ...config, address: v })} /></div>
        </div>
      </div>

      <div className="rounded-2xl p-6 bg-white" style={CARD}>
        <h3 className="text-sm font-semibold mb-5" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>Reseaux sociaux</h3>
        <div className="grid sm:grid-cols-3 gap-5">
          <Field label="Facebook" value={config.social_links?.facebook || ''} onChange={v => setConfig({ ...config, social_links: { ...config.social_links, facebook: v } })} />
          <Field label="Instagram" value={config.social_links?.instagram || ''} onChange={v => setConfig({ ...config, social_links: { ...config.social_links, instagram: v } })} />
          <Field label="LinkedIn" value={config.social_links?.linkedin || ''} onChange={v => setConfig({ ...config, social_links: { ...config.social_links, linkedin: v } })} />
        </div>
      </div>

      <div className="rounded-2xl p-6 bg-white" style={CARD}>
        <h3 className="text-sm font-semibold mb-5" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>SEO</h3>
        <div className="space-y-5">
          <Field label="Meta Title" value={config.seo?.meta_title || ''} onChange={v => setConfig({ ...config, seo: { ...config.seo, meta_title: v } })} />
          <Field label="Meta Description" value={config.seo?.meta_description || ''} area onChange={v => setConfig({ ...config, seo: { ...config.seo, meta_description: v } })} />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-config-btn"
          className="flex items-center gap-2 px-8 py-3 rounded-xl text-[13px] font-semibold text-white"
          style={{ background: T.vert, fontFamily: "'Montserrat', sans-serif" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
        </button>
      </div>
    </div>
  );
};

/* ━━━ MEDIA ━━━ */
const MediaTab = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => { setLoading(true); try { setImages(await adminApi.get('/api/admin/media')); } catch (err) { console.error('Failed to load media:', err); } setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await adminApi.upload('/api/admin/media', formData);
      }
      await load();
    } catch (err) { console.error('Failed to upload media:', err); }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try { await adminApi.delete(`/api/admin/media/${id}`); await load(); } catch (err) { console.error('Failed to delete media:', err); }
  };

  return (
    <div className="space-y-6" data-testid="media-tab">
      <div className="rounded-2xl p-8 text-center bg-white" style={{ ...CARD, borderStyle: 'dashed' }}>
        <input type="file" multiple accept="image/*" onChange={e => upload(Array.from(e.target.files))} className="hidden" id="media-upload" />
        <label htmlFor="media-upload" className="cursor-pointer">
          <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: T.textMuted }} />
          <p className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textSecondary }}>
            {uploading ? 'Envoi en cours...' : 'Cliquez pour telecharger'}
          </p>
          <p className="text-[11px] mt-1" style={{ color: T.textMuted }}>PNG, JPG, WebP</p>
        </label>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div> : (
        images.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-white" style={CARD}>
            <ImageIcon className="w-10 h-10 mx-auto mb-3" style={{ color: T.border }} />
            <p className="text-sm" style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>Aucun media</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map(img => (
              <div key={img.media_id || img.url} className="relative group rounded-xl overflow-hidden" style={{ ...CARD, aspectRatio: '1' }}>
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button onClick={() => remove(img.media_id)} className="p-2 rounded-full bg-white/90" style={{ color: '#ef4444' }}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default AdminSettingsPage;
